import { Moment } from 'moment';
import moment from 'moment';
import React, { forwardRef, useContext, useImperativeHandle, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Text } from '../../../components/Text';
import { TextInput } from '../../../components/TextInput';
import { DateRangePickerContext } from '../context';
import { Focus, FocusPosition, MomentRange, RangeIndex } from '../types';
import { DateInput } from './DateInput';
import { TimeInput } from './TimeInput';

interface DateRangeInputProps {
  range: MomentRange;
  rangeIndex: RangeIndex;
  onChange: (day: Moment, preventNext?: boolean) => void;
  onFocus: (focus: Focus) => void;
  onFocusNext: () => void;
  /**
   * If `true`, the second value in the date range picker will be set to 'ongoing'
   * @default false
   */
  ongoing?: boolean;
}

export const DateRangeInput = forwardRef<
  { focus: (position: FocusPosition) => void; blur: (position: FocusPosition) => void },
  DateRangeInputProps
>(({ range, rangeIndex, onChange, onFocus, onFocusNext, ongoing }, ref) => {
  const startRef = useRef<{ focus: () => void; blur: () => {} }>(null);
  const startTimeRef = useRef<{ focus: () => void; blur: () => {} }>(null);
  const endRef = useRef<{ focus: () => void; blur: () => void }>(null);
  const endTimeRef = useRef<{ focus: () => void; blur: () => {} }>(null);
  const intl = useIntl();
  const { includeTimeInputs } = useContext(DateRangePickerContext);

  useImperativeHandle(ref, () => ({
    focus: position => {
      if (position === FocusPosition.Start) {
        startRef.current?.focus();
      } else if (position === FocusPosition.StartTime) {
        startTimeRef.current?.focus();
      } else if (position === FocusPosition.End) {
        endRef.current?.focus();
      } else if (position === FocusPosition.EndTime) {
        endTimeRef.current?.focus();
      }
    },
    blur: position => {
      if (position === FocusPosition.Start) {
        startRef.current?.blur();
      } else if (position === FocusPosition.StartTime) {
        startTimeRef.current?.blur();
      } else if (position === FocusPosition.End) {
        endRef.current?.blur();
      } else if (position === FocusPosition.EndTime) {
        endTimeRef.current?.blur();
      }
    },
  }));

  let rangeEndTime = range.end?.format('hh:mm A').toString();
  if (!rangeEndTime) {
    rangeEndTime = '12:00 AM';
  }

  return (
    <>
      <DateInput
        ref={startRef}
        date={range.start}
        onChange={onChange}
        onFocus={() => onFocus([rangeIndex, FocusPosition.Start])}
        onFocusNext={onFocusNext}
      />
      {includeTimeInputs && (
        <TimeInput
          ref={startTimeRef}
          time={range.start?.format('hh:mm A').toString() || '12:00 AM'}
          onFocus={() => onFocus([rangeIndex, FocusPosition.StartTime])}
          onFocusNext={onFocusNext}
          onChange={(time: string, preventNext?: boolean) => {
            const momentTime = moment(time, 'hh:mm A');
            const newStart = range.start?.clone();
            if (newStart) {
              newStart.set({
                hour: momentTime.get('hour'),
                minute: momentTime.get('minute'),
                second: momentTime.get('second'),
                millisecond: momentTime.get('millisecond'),
              });
              return onChange(newStart, preventNext);
            }
          }}
          rangeKey={rangeIndex}
          positionKey={FocusPosition.StartTime}
        />
      )}
      <Text appearance="bodyMedium" tag="div">
        –
      </Text>{' '}
      {ongoing ? (
        <>
          <TextInput
            testId="date-range-input-ongoing"
            value={intl.formatMessage({ defaultMessage: 'Ongoing', id: 'VuzjdC' })}
            disabled
          />
          {includeTimeInputs && <div /> /** Empty tag for the grid layout */}
        </>
      ) : (
        <>
          <DateInput
            ref={endRef}
            date={range.end}
            onChange={onChange}
            onFocus={() => onFocus([rangeIndex, FocusPosition.End])}
            onFocusNext={onFocusNext}
          />
          {includeTimeInputs && (
            <TimeInput
              ref={endTimeRef}
              time={rangeEndTime}
              onFocus={() => onFocus([rangeIndex, FocusPosition.EndTime])}
              onFocusNext={onFocusNext}
              onChange={(time: string) => {
                const momentTime = moment(time, 'hh:mm A');
                const newEnd = range.end?.clone();
                if (newEnd) {
                  newEnd.set({
                    hour: momentTime.get('hour'),
                    minute: momentTime.get('minute'),
                    second: momentTime.get('second'),
                    millisecond: momentTime.get('millisecond'),
                  });
                  return onChange(newEnd);
                }
              }}
              rangeKey={rangeIndex}
              positionKey={FocusPosition.EndTime}
            />
          )}
        </>
      )}
    </>
  );
});
DateRangeInput.displayName = 'DateRangeInput';
